import React from 'react';
import PropTypes from 'prop-types';

import './MobileLegendsRankPicker.sass';

import warriorLogo from './assets/warrior.png';
import eliteLogo from './assets/elite.png';
import masterLogo from './assets/master.png';
import grandmasterLogo from './assets/grandmaster.png';
import epicLogo from './assets/epic.png';
import legendLogo from './assets/legend.png';
import mythicLogo from './assets/mythic.png';

import FieldSelect from '../../../fields/fieldSelect/FieldSelect';
import {FormattedMessage} from 'react-intl';


const firstRankIdx = 1;
const rankSpecs = {
  stars_five: [{label: '0 Stars'}, {label: '1 Star'}, {label: '2 Stars'}, {label: '3 Stars'}, {label: '4 Stars'}, {label: '5 Stars'}],
  stars_four: [{label: '0 Stars'}, {label: '1 Star'}, {label: '2 Stars'}, {label: '3 Stars'}, {label: '4 Stars'}],
  stars_three: [{label: '0 Stars'}, {label: '1 Star'}, {label: '2 Stars'}, {label: '3 Stars'}],
  divisions_five: [
    {label: 'Division V'},
    {label: 'Division IV'},
    {label: 'Division III'},
    {label: 'Division II'},
    {label: 'Division I'},
  ],
  divisions_five_stars_five: [
    {label: 'Division V', children: 'stars_five'},
    {label: 'Division IV', children: 'stars_five'},
    {label: 'Division III', children: 'stars_five'},
    {label: 'Division II', children: 'stars_five'},
    {label: 'Division I', children: 'stars_five'},
  ],
  divisions_four_stars_four: [
    {label: 'Division IV', children: 'stars_four'},
    {label: 'Division III', children: 'stars_four'},
    {label: 'Division II', children: 'stars_four'},
    {label: 'Division I', children: 'stars_four'},
  ],
  divisions_three_stars_four: [
    {label: 'Division III', children: 'stars_four'},
    {label: 'Division II', children: 'stars_four'},
    {label: 'Division I', children: 'stars_four'},
  ],
  divisions_three_stars_three: [
    {label: 'Division III', children: 'stars_three'},
    {label: 'Division II', children: 'stars_three'},
    {label: 'Division I', children: 'stars_three'},
  ],
  root: [
    {label: 'Warrior', image: warriorLogo, children: 'divisions_three_stars_three'},
    {label: 'Elite', image: eliteLogo, children: 'divisions_three_stars_four'},
    {label: 'Master', image: masterLogo, children: 'divisions_four_stars_four'},
    {label: 'Grand Master', image: grandmasterLogo, children: 'divisions_five_stars_five'},
    {label: 'Epic', image: epicLogo, children: 'divisions_five_stars_five'},
    {label: 'Legend', image: legendLogo, children: 'divisions_five_stars_five'},
    {label: 'Mythic', image: mythicLogo, children: 'divisions_five'},
  ],
};

export default class MobileLegendsRankPicker extends React.PureComponent {
  static mappedSpecs = null;

  static getMappedSpecs = () => {
    if (!MobileLegendsRankPicker.mappedSpecs) {
      MobileLegendsRankPicker.mappedSpecs = MobileLegendsRankPicker.mapSpecsNode(
        firstRankIdx,
        {children: rankSpecs.root},
        rankSpecs
      );
    }

    return MobileLegendsRankPicker.mappedSpecs;
  };

  static mapSpecsNode = (startIdx, node, specs) => {
    if (!node.children) {
      return {...node, min: startIdx, max: startIdx};
    }

    const childNodes = typeof node.children === 'string'
      ? specs[node.children]
      : node.children;

    let currentMin = startIdx;
    const mappedChildren = childNodes.map(child => {
      const childMapping = MobileLegendsRankPicker.mapSpecsNode(currentMin, child, specs);

      currentMin = childMapping.max + 1;

      return childMapping;
    });

    return {
      ...node,
      min: mappedChildren[0].min,
      max: mappedChildren[mappedChildren.length - 1].max,
      children: mappedChildren
    };
  };

  static getNodesForRank = rank => {
    const nodes = [];
    let currentNode = MobileLegendsRankPicker.getMappedSpecs();

    if (rank < currentNode.min || rank > currentNode.max) {
      return nodes;
    }

    while (currentNode && currentNode.children) {
      const newCurrentNode = currentNode.children.find(childNode => rank >= childNode.min && rank <= childNode.max);

      if (newCurrentNode) {
        nodes.push(newCurrentNode);
      }

      currentNode = newCurrentNode;
    }

    return nodes;
  };

  static getRankByNodePath = nodeIdxList => {
    let currentNode = MobileLegendsRankPicker.getMappedSpecs();

    for (let nodeIdx of nodeIdxList) {
      currentNode = currentNode.children[nodeIdx];
      if (!currentNode) {
        return false;
      }
    }

    return currentNode.min;
  };

  static describeRank = rank => MobileLegendsRankPicker
    .getNodesForRank(rank)
    .map(node => node.label)
    .join(' ');

  static getDefaultValue = () => [36, 108];

  onFromChange = newFromValue => {
    const {onChange, value} = this.props;

    onChange([
      newFromValue,
      value[1] < newFromValue ? newFromValue + 1 : value[1]
    ]);
  };

  onToChange = newToValue => {
    const {onChange, value} = this.props;

    onChange([
      value[0],
      newToValue
    ]);
  };

  renderRankImage = (nodes) => {
    const imageNode = nodes.reduce((acc, val) => val.image ? val : acc);

    return <img src={imageNode.image} alt={imageNode.label}/>;
  };

  renderRankInputs = (nodes, callback, lowerBoundary, upperBoundary) => {
    const specs = MobileLegendsRankPicker.getMappedSpecs();
    let currentChildren = specs.children;

    lowerBoundary = lowerBoundary || specs.min;
    upperBoundary = upperBoundary || specs.max;

    return nodes.map((node, nodeIdx) => {
      const selectedValueIdx = currentChildren.findIndex(value => value === node);
      const children = currentChildren;
      currentChildren = node.children;

      return (
        <div className="form-field" key={nodeIdx}>
          <FieldSelect
            value={selectedValueIdx}
            onChange={evt => callback(children[parseInt(evt.target.value)].min)}
          >
            {children
              .map((child, childIdx) => child.max < lowerBoundary || child.min > upperBoundary ? false : (
                <option key={childIdx} value={childIdx}>
                  {child.label}
                </option>
              ))}
          </FieldSelect>
        </div>
      );
    });
  };

  render() {
    const {value} = this.props;

    const fromNodes = MobileLegendsRankPicker.getNodesForRank(value[0]);
    const toNodes = MobileLegendsRankPicker.getNodesForRank(value[1]).slice(0, 2);

    const specs = MobileLegendsRankPicker.getMappedSpecs();

    return (
      <div className="converter">
        <div className="converter-rank-img">
          {this.renderRankImage(fromNodes)}
        </div>

        <div className="converter-item  converter-item--select">
          <label>
            <FormattedMessage id="section.product.rank.current"/>
          </label>
          {this.renderRankInputs(fromNodes, this.onFromChange, specs.min, specs.max - 1)}
        </div>

        <div className="converter-divider"/>

        <div className="converter-item  converter-item--select">
          <label>
            <FormattedMessage id="section.product.wanted"/>
          </label>
          {this.renderRankInputs(toNodes, this.onToChange, value[0] + 1)}
        </div>

        <div className="converter-rank-img">
          {this.renderRankImage(toNodes)}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./components/productVariants/rankPickers/mobileLegends/MobileLegendsRankPicker.js